import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Wrench, Search } from 'lucide-react';

interface Plan {
  id: string;
  name: string;
  credits: number;
}

export const SubscriptionFixer = () => {
  const [email, setEmail] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [userInfo, setUserInfo] = useState<any>(null);

  const loadPlans = async () => {
    const { data, error } = await supabase
      .from('plans')
      .select('id, name, credits')
      .eq('is_active', true)
      .order('name');
    
    if (error) {
      console.error('Error loading plans:', error);
      return;
    }
    
    setPlans(data || []);
  };

  const searchUser = async () => {
    if (!email) {
      toast.error('Please enter an email address');
      return;
    }

    setLoading(true);
    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('user_id, email, full_name')
        .eq('email', email)
        .maybeSingle();

      if (profileError) {
        console.error('Profile error:', profileError);
        toast.error('Error searching for user');
        return;
      }

      if (!profile) {
        toast.error('User not found');
        setUserInfo(null);
        return;
      }

      const { data: subscription, error: subError } = await supabase
        .from('user_subscriptions')
        .select(`
          *,
          plans!inner(name, credits)
        `)
        .eq('user_id', profile.user_id)
        .eq('status', 'active')
        .maybeSingle();

      if (subError) {
        console.error('Subscription error:', subError);
      }

      setUserInfo({
        profile,
        subscription
      });

      if (!subscription) {
        toast.warning('User found but no active subscription');
      } else {
        toast.success('User found with active subscription');
      }

    } catch (error) {
      console.error('Search error:', error);
      toast.error('Error searching for user');
    } finally {
      setLoading(false);
    }
  };

  const fixSubscription = async () => {
    if (!userInfo || !selectedPlan) {
      toast.error('Please search for a user and select a plan first');
      return;
    }

    if (!plans.length) {
      await loadPlans();
    }

    const plan = plans.find(p => p.id === selectedPlan);
    if (!plan) {
      toast.error('Selected plan not found');
      return;
    }

    setLoading(true);
    try {
      const creditsRemaining = plan.credits === -1 ? null : plan.credits;
      
      const subscriptionData = {
        user_id: userInfo.profile.user_id,
        plan_id: plan.id,
        status: 'active',
        billing_cycle: billingCycle,
        credits_remaining: creditsRemaining,
        credits_used: 0,
        updated_at: new Date().toISOString(),
      };

      if (userInfo.subscription) {
        // Update existing subscription
        const { error } = await supabase
          .from('user_subscriptions')
          .update(subscriptionData)
          .eq('user_id', userInfo.profile.user_id)
          .eq('status', 'active');

        if (error) throw error;
        toast.success('Subscription updated successfully');
      } else {
        // Create new subscription
        const { error } = await supabase
          .from('user_subscriptions')
          .insert([subscriptionData]);

        if (error) throw error;
        toast.success('Subscription created successfully');
      }

      // Refresh user info
      await searchUser();
      
    } catch (error) {
      console.error('Fix error:', error);
      toast.error('Error fixing subscription');
    } finally {
      setLoading(false);
    }
  };

  // Load plans on component mount
  useState(() => {
    loadPlans();
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wrench className="w-5 h-5" />
          Subscription Fixer
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">User Email</Label>
          <div className="flex gap-2">
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
            />
            <Button onClick={searchUser} disabled={loading}>
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </div>
        </div>

        {userInfo && (
          <div className="border rounded p-3 bg-muted/50">
            <h4 className="font-medium mb-2">User Information</h4>
            <p><strong>Name:</strong> {userInfo.profile.full_name || 'Not set'}</p>
            <p><strong>Email:</strong> {userInfo.profile.email}</p>
            <p><strong>Current Plan:</strong> {userInfo.subscription ? userInfo.subscription.plans.name : 'No active plan'}</p>
            <p><strong>Credits:</strong> {userInfo.subscription ? 
              (userInfo.subscription.credits_remaining === null ? 'Unlimited' : userInfo.subscription.credits_remaining) : 
              'N/A'
            }</p>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="plan">New Plan</Label>
          <Select value={selectedPlan} onValueChange={setSelectedPlan}>
            <SelectTrigger>
              <SelectValue placeholder="Select a plan" />
            </SelectTrigger>
            <SelectContent>
              {plans.map((plan) => (
                <SelectItem key={plan.id} value={plan.id}>
                  {plan.name} ({plan.credits === -1 ? 'Unlimited' : plan.credits} credits)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="billing">Billing Cycle</Label>
          <Select value={billingCycle} onValueChange={setBillingCycle}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="yearly">Yearly</SelectItem>
              <SelectItem value="lifetime">Lifetime</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button 
          onClick={fixSubscription} 
          disabled={loading || !userInfo || !selectedPlan}
          className="w-full"
        >
          <Wrench className="w-4 h-4 mr-2" />
          Fix Subscription
        </Button>
      </CardContent>
    </Card>
  );
};